<template>
  <div>
    <page-toolbar class="mb-4">
      <template #filters>
        <v-text-field
          :value="search"
          label="Buscar"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="finance-filter br-form-field"
          @input="$emit('update:search', $event)"
          @keyup.enter="$emit('load')"
          @click:clear="$emit('load')"
        />
        <v-select
          :value="statusFilter"
          :items="getStatusOptions()"
          item-text="label"
          item-value="value"
          label="Status"
          outlined
          dense
          hide-details
          clearable
          class="finance-filter br-form-field"
          @input="$emit('update:statusFilter', $event)"
          @change="$emit('load')"
        />
      </template>
      <template #actions>
        <v-btn dark color="primary" depressed @click="$emit('new')">
          <v-icon left>mdi-plus</v-icon>
          Novo lançamento
        </v-btn>
      </template>
    </page-toolbar>

    <data-table-card
      :headers="tableHeaders()"
      :items="entries"
      :loading="loadingEntries"
      :items-per-page="10"
      show-count
      no-data-text="Nenhum lançamento encontrado"
    >
      <template v-slot:[`item.amount`]="{ item }">
        {{ formatPrice(item.amount) }}
      </template>
      <template v-slot:[`item.dueDate`]="{ item }">
        <span :class="{ 'orange--text text--darken-2 font-weight-medium': isOverdue(item) }">
          {{ formatDate(item.dueDate) }}
        </span>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <v-chip small :color="statusColor(item.status)" dark>
          {{ statusLabel(item.status) }}
        </v-chip>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <template v-if="item.status === 'PENDING'">
          <v-btn
            icon
            small
            :title="entryType === 'payable' ? 'Marcar como paga' : 'Marcar como recebida'"
            @click="$emit('mark-paid', item)"
          >
            <v-icon color="success">mdi-check-circle</v-icon>
          </v-btn>
          <v-btn icon small title="Editar" @click="$emit('open-form', item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small title="Cancelar" @click="$emit('cancel', item)">
            <v-icon color="grey">mdi-close-circle</v-icon>
          </v-btn>
        </template>
        <span v-else class="grey--text text-caption">—</span>
      </template>
    </data-table-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import DataTableCard from "@/components/ui/DataTableCard.vue";
import PageToolbar from "@/components/ui/PageToolbar.vue";
import { FinanceEntryStatus, FinanceEntryType } from "@/types/finance/Finance";

export default defineComponent({
  components: { DataTableCard, PageToolbar },

  props: {
    entryType: {
      type: String as PropType<"payable" | "receivable">,
      required: true,
    },
    entries: {
      type: Array as PropType<FinanceEntryType[]>,
      default: () => [],
    },
    loadingEntries: Boolean,
    statusFilter: String,
    search: String,
    counterpartyLabel: {
      type: String,
      default: "Fornecedor",
    },
  },
  methods: {
    tableHeaders() {
      return [
        { text: "Descrição", value: "description" },
        { text: this.counterpartyLabel, value: "counterpartyName" },
        { text: "Valor", value: "amount" },
        { text: "Vencimento", value: "dueDate" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false },
      ];
    },
    getStatusOptions() {
      return [
        { value: "PENDING", label: "Pendente" },
        { value: "PAID", label: this.entryType === "payable" ? "Paga" : "Recebida" },
        { value: "CANCELLED", label: "Cancelada" },
      ];
    },
    formatPrice(value: number) {
      return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDate(value: string) {
      if (!value) return "";
      const [y, m, d] = value.split("-");
      return `${d}/${m}/${y}`;
    },
    isOverdue(item: FinanceEntryType) {
      if (item.status !== "PENDING") return false;
      const today = new Date().toISOString().slice(0, 10);
      return item.dueDate < today;
    },
    statusColor(status: FinanceEntryStatus) {
      if (status === "PAID") return "success";
      if (status === "CANCELLED") return "grey";
      return "warning";
    },
    statusLabel(status: FinanceEntryStatus) {
      if (status === "PAID") return this.entryType === "payable" ? "Paga" : "Recebida";
      if (status === "CANCELLED") return "Cancelada";
      return "Pendente";
    },
  },
});
</script>

<style scoped lang="scss">
.finance-filter {
  min-width: 180px;
  max-width: 260px;
}
</style>
